import './postausgang.css';
import { useEffect, useState } from 'react';
import { api, errorText } from '../../api';
import { fmtDate, fmtTime, plural } from '../../lib/format';
import { eurC } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import type { AusgangBrief, AusgangResponse } from './types';

// Postausgang (nur Admin, Sam 14.10.: „mach erstmal das wir als kanzlei das selber raussenden“): Briefe, die Mandanten
// im Rechnungsprogramm „Per Post“ verschicken, druckt die Kanzlei selbst aus. Einzeln („PDF“) oder alle in einer PDF
// („Alle drucken“, immer einseitig), dann in Fensterumschläge, zur Post und „Verschickt“ bzw. „Gedruckte verschickt“
// klicken; erst dann kommen sie auf die Monatsrechnung des Mandanten. Geht ein Brief nicht raus (z. B. Anschrift
// falsch): „Nicht verschicken“, dann wird nichts berechnet und der Mandant sieht es. Darunter die verschickten der
// letzten 30 Tage zum Nachsehen.

const API = '/api/kanzlei/postausgang';
// so viele Briefe höchstens in einer PDF bzw. auf einmal als verschickt (wie der Server, postausgang.ts MAX_STAPEL);
// bei mehr die ältesten zuerst, danach rücken die nächsten nach
const STAPEL = 200;

function anWen(b: AusgangBrief) {
  if (!b.empfaenger) return 'Empfänger: siehe PDF';
  return `An ${b.empfaenger.name}${b.empfaenger.ort ? `, ${b.empfaenger.ort}` : ''}`;
}
const beleg = (b: AusgangBrief) => (b.beleg ? `${b.beleg.art} ${b.beleg.nummer}` : 'Rechnung gelöscht');
const zeit = (v: string) => `${fmtDate(v)}, ${fmtTime(v)} Uhr`;

export function Postausgang() {
  const toast = useToast();
  const res = useApi<AusgangResponse>(API, { live: ['postausgang', 'clients'] });
  const [busy, setBusy] = useState(false);
  // die Briefe der PDF von „Alle drucken“ (beim letzten Klick auf dieser Seite). Nur sie markiert „Gedruckte
  // verschickt“: Was danach dazukommt (die Liste lädt live nach), ist nicht gedruckt und bleibt im Postausgang.
  const [gedruckt, setGedruckt] = useState<string[] | null>(null);
  // „Gedruckte verschickt“ bestätigen: die Briefe beim Klick
  const [alle, setAlle] = useState<string[] | null>(null);
  // „Verschickt“ bzw. „Nicht verschicken“ für einen Brief bestätigen (beides lässt sich nicht zurücknehmen)
  const [frage, setFrage] = useState<{ id: string; ende: 'verschickt' | 'zurueck' } | null>(null);

  useEffect(() => {
    document.title = 'Postausgang · Elbcons Kanzlei';
  }, []);

  const d = res.data;
  const offen = d?.offen ?? [];
  const verschickt = d?.verschickt ?? [];
  const ids = offen.slice(0, STAPEL).map((b) => b.id);
  // gedruckt und noch nicht erledigt
  const stapel = (gedruckt ?? []).filter((id) => offen.some((b) => b.id === id));
  // beim Bestätigen: so viele Briefe sind nicht in der PDF und bleiben
  const rest = alle ? offen.filter((b) => !alle.includes(b.id)).length : 0;

  // run: die Aktion, gibt die Meldung zurück; danach (auch bei Fehler, z. B. schon erledigt) die Liste neu laden
  const act = async (run: () => Promise<string>) => {
    setBusy(true);
    try {
      toast(await run());
    } catch (err) {
      toast(errorText(err));
    } finally {
      await res.reload();
      setBusy(false);
    }
  };

  const einer = (b: AusgangBrief) =>
    act(async () => {
      await api.post(`${API}/${b.id}/verschickt`);
      setFrage(null);
      return `Verschickt: ${b.mandant.name} · kommt auf die Monatsrechnung`;
    });
  const zurueck = (b: AusgangBrief) =>
    act(async () => {
      await api.post(`${API}/${b.id}/zurueck`);
      setFrage(null);
      return `Nicht verschickt: ${b.mandant.name} · wird nicht berechnet, der Mandant sieht es`;
    });
  const alleVerschickt = (list: string[]) =>
    act(async () => {
      const r = await api.post<{ verschickt: number }>(`${API}/verschickt`, { ids: list });
      setAlle(null);
      const n = `${plural(r.verschickt, 'Brief', 'Briefe')} als verschickt markiert`;
      return r.verschickt < list.length ? `${n}, die übrigen waren schon erledigt` : n;
    });

  return (
    <div className="pa">
      <div className="head">
        <div>
          <h1>Postausgang</h1>
          <p>
            Briefe, die Mandanten im Rechnungsprogramm „Per Post“ verschicken. Ausdrucken, in Fensterumschläge (DIN lang)
            stecken, frankieren, zur Post bringen und „Verschickt“ klicken.
          </p>
        </div>
      </div>
      {!d ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          <div className="card">
            <div className="ch">
              <h3>{offen.length ? `Wartet auf den Druck (${offen.length})` : 'Wartet auf den Druck'}</h3>
              {offen.length > 0 && (
                <div className="pa-alle">
                  <a
                    className="btn sm"
                    href={`${API}/stapel?ids=${ids.join(',')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setGedruckt(ids)}
                  >
                    Alle drucken ({ids.length})
                  </a>
                  {!alle && stapel.length > 0 && (
                    <button type="button" className="btn sm dark" disabled={busy} onClick={() => setAlle(stapel)}>
                      Gedruckte verschickt ({stapel.length})
                    </button>
                  )}
                </div>
              )}
            </div>
            {alle && (
              <div className="pa-ok" role="alert">
                <span>
                  {alle.length === 1
                    ? `Den gedruckten Brief als verschickt markieren? Er kommt mit ${eurC(d.preis)} netto auf die Monatsrechnung des Mandanten.`
                    : `Die ${alle.length} gedruckten Briefe als verschickt markieren? Sie kommen mit je ${eurC(d.preis)} netto auf die Monatsrechnung der Mandanten.`}
                  {rest > 0 &&
                    (rest === 1
                      ? ' 1 neuerer Brief ist noch nicht gedruckt und bleibt im Postausgang.'
                      : ` ${rest} neuere Briefe sind noch nicht gedruckt und bleiben im Postausgang.`)}
                </span>
                <span className="row">
                  <button type="button" className="btn sm dark" disabled={busy} onClick={() => void alleVerschickt(alle)}>
                    Ja, verschickt
                  </button>
                  <button type="button" className="btn sm" onClick={() => setAlle(null)}>
                    Abbrechen
                  </button>
                </span>
              </div>
            )}
            {offen.length > 0 && (
              <p className="note">
                „Alle drucken“ ist eine PDF mit allen Briefen: einseitig und in Originalgröße (100 %) drucken, dann beginnt
                jeder Brief auf einem neuen Blatt und die Anschrift sitzt im Fenster. „Gedruckte verschickt“ markiert danach
                genau die Briefe aus dieser PDF.
              </p>
            )}
            {!offen.length && <p className="note">Keine Briefe zu drucken.</p>}
            {offen.map((b) => (
              <div key={b.id} className="task pa-r" data-testid="pa-row">
                <span className="pa-i" aria-hidden="true">
                  ✉
                </span>
                <div className="tx">
                  <b>{b.mandant.name}</b>
                  <small className="pa-an">{anWen(b)}</small>
                  <small>
                    {beleg(b)} · {plural(b.seiten, 'Seite', 'Seiten')} · {b.farbe ? 'Farbe' : 'schwarz-weiß'} · einseitig · seit{' '}
                    {zeit(b.seit)}
                  </small>
                </div>
                <div className="pa-a">
                  <a className="btn sm" href={`${API}/${b.id}/pdf`} target="_blank" rel="noopener noreferrer">
                    PDF
                  </a>
                  {frage?.id !== b.id && (
                    <>
                      <button type="button" className="btn sm dark" disabled={busy} onClick={() => setFrage({ id: b.id, ende: 'verschickt' })}>
                        Verschickt
                      </button>
                      <button type="button" className="btn sm pa-nein" disabled={busy} onClick={() => setFrage({ id: b.id, ende: 'zurueck' })}>
                        Nicht verschicken
                      </button>
                    </>
                  )}
                </div>
                {frage?.id === b.id && (
                  <div className="pa-ok pa-frage" role="alert">
                    <span>
                      {frage.ende === 'verschickt'
                        ? `Diesen Brief als verschickt markieren? Er kommt mit ${eurC(d.preis)} netto auf die Monatsrechnung des Mandanten.`
                        : 'Diesen Brief nicht verschicken (z. B. Anschrift falsch)? Er wird nicht berechnet, der Mandant sieht „nicht verschickt“.'}
                    </span>
                    <span className="row">
                      <button
                        type="button"
                        className="btn sm dark"
                        disabled={busy}
                        onClick={() => void (frage.ende === 'verschickt' ? einer(b) : zurueck(b))}
                      >
                        {frage.ende === 'verschickt' ? 'Ja, verschickt' : 'Ja, nicht verschicken'}
                      </button>
                      <button type="button" className="btn sm" onClick={() => setFrage(null)}>
                        Abbrechen
                      </button>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="card">
            <div className="ch">
              <h3>Verschickt in den letzten 30 Tagen</h3>
            </div>
            {!verschickt.length && <p className="note">Noch nichts verschickt.</p>}
            {verschickt.map((b) => (
              <div key={b.id} className="task pa-r pa-fertig" data-testid="pa-sent">
                <span className="pa-i" aria-hidden="true">
                  ✓
                </span>
                <div className="tx">
                  <b>{b.mandant.name}</b>
                  <small className="pa-an">{anWen(b)}</small>
                  <small>
                    {beleg(b)} · {plural(b.seiten, 'Seite', 'Seiten')} · verschickt am {b.verschicktAm ? zeit(b.verschicktAm) : '–'}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
